import React, { useState, useCallback, useRef } from 'react';
import Cropper from 'react-easy-crop';
import { FaTimes, FaCheck, FaCamera, FaTrash } from 'react-icons/fa';
import { Area } from 'react-easy-crop';

interface LogoEditorModalProps {
  isOpen: boolean;
  currentLogoUrl?: string | null;
  onClose: () => void;
  onSave: (croppedImageBlob: Blob) => void;
  onDelete: () => void;
}

export default function LogoEditorModal({ isOpen, currentLogoUrl, onClose, onSave, onDelete }: LogoEditorModalProps) {
  const [imageUrl, setImageUrl] = useState<string>(currentLogoUrl || '');
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(0.5);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (isOpen) {
      setImageUrl(currentLogoUrl || '');
      setCrop({ x: 0, y: 0 });
      setZoom(0.5);
    }
  }, [isOpen, currentLogoUrl]);

  const onCropComplete = useCallback((_croppedArea: Area, croppedAreaPixels: Area) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      setImageUrl(reader.result as string);
      setCrop({ x: 0, y: 0 });
      setZoom(0.5);
    };
    reader.readAsDataURL(file);
  };

  const createCroppedImage = async (): Promise<Blob | null> => {
    if (!croppedAreaPixels || !imageUrl) return null;

    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.src = imageUrl;

    await new Promise((resolve) => {
      image.onload = resolve;
    });

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    const outputWidth = 1725;
    const outputHeight = 350;
    canvas.width = outputWidth;
    canvas.height = outputHeight;

    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, outputWidth, outputHeight);

    const srcX = Math.max(0, croppedAreaPixels.x);
    const srcY = Math.max(0, croppedAreaPixels.y);
    const srcWidth = Math.min(croppedAreaPixels.width, image.naturalWidth - srcX);
    const srcHeight = Math.min(croppedAreaPixels.height, image.naturalHeight - srcY);

    const dstX = croppedAreaPixels.x < 0 ? (-croppedAreaPixels.x / croppedAreaPixels.width) * outputWidth : 0;
    const dstY = croppedAreaPixels.y < 0 ? (-croppedAreaPixels.y / croppedAreaPixels.height) * outputHeight : 0;
    const dstWidth = (srcWidth / croppedAreaPixels.width) * outputWidth;
    const dstHeight = (srcHeight / croppedAreaPixels.height) * outputHeight;

    ctx.drawImage(
      image,
      srcX,
      srcY,
      srcWidth,
      srcHeight,
      dstX,
      dstY,
      dstWidth,
      dstHeight
    );

    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        resolve(blob);
      }, 'image/png', 1);
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const croppedBlob = await createCroppedImage();
      if (croppedBlob) {
        onSave(croppedBlob);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = () => {
    if (window.confirm('Remove company logo?')) {
      onDelete();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.9)',
        zIndex: 2000,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: 20,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        }}
      >
        <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: '#fff' }}>
          Edit company logo
        </h3>
        <button
          onClick={onClose}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: 8,
            color: '#fff',
          }}
        >
          <FaTimes size={18} />
        </button>
      </div>

      {/* Crop Area or Empty State */}
      <div style={{ position: 'relative', flex: 1 }}>
        {imageUrl ? (
          <Cropper
            image={imageUrl}
            crop={crop}
            zoom={zoom}
            minZoom={0.1}
            maxZoom={5}
            aspect={1725 / 350}
            cropShape="rect"
            showGrid={true}
            onCropChange={setCrop}
            onCropComplete={onCropComplete}
            onZoomChange={setZoom}
            objectFit="contain"
            restrictPosition={false}
          />
        ) : (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              color: '#fff',
            }}
          >
            <FaCamera size={60} style={{ opacity: 0.3, marginBottom: 20 }} />
            <p style={{ fontSize: 16, marginBottom: 20 }}>No logo uploaded</p>
            <button
              onClick={() => fileInputRef.current?.click()}
              style={{
                padding: '12px 24px',
                background: '#021a4a',
                border: 'none',
                borderRadius: 10,
                cursor: 'pointer',
                fontSize: 14,
                fontWeight: 600,
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <FaCamera size={14} />
              Upload logo
            </button>
          </div>
        )}
      </div>

      {/* Hidden File Input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        style={{ display: 'none' }}
      />

      {/* Controls */}
      <div
        style={{
          padding: 24,
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
        }}
      >
        {/* Zoom Slider - only show when image exists */}
        {imageUrl && (
          <div style={{ marginBottom: 24 }}>
            <label
              style={{
                display: 'block',
                marginBottom: 8,
                fontSize: 13,
                fontWeight: 500,
                color: '#fff',
              }}
            >
              Zoom
            </label>
            <input
              type="range"
              min={0.1}
              max={5}
              step={0.01}
              value={zoom}
              onChange={(e) => setZoom(parseFloat(e.target.value))}
              style={{
                width: '100%',
                cursor: 'pointer',
              }}
            />
          </div>
        )}

        {/* Action Buttons */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', gap: 12 }}>
            {imageUrl && (
              <>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={saving}
                  style={{
                    padding: '12px 24px',
                    background: 'rgba(255, 255, 255, 0.1)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: 10,
                    cursor: saving ? 'not-allowed' : 'pointer',
                    fontSize: 14,
                    fontWeight: 600,
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                  }}
                >
                  <FaCamera size={14} />
                  New image
                </button>

                {currentLogoUrl && (
                  <button
                    onClick={handleDelete}
                    disabled={saving}
                    style={{
                      padding: '12px 24px',
                      background: 'rgba(244, 67, 54, 0.1)',
                      border: '1px solid rgba(244, 67, 54, 0.3)',
                      borderRadius: 10,
                      cursor: saving ? 'not-allowed' : 'pointer',
                      fontSize: 14,
                      fontWeight: 600,
                      color: '#f44336',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                    }}
                  >
                    <FaTrash size={14} />
                    Delete
                  </button>
                )}
              </>
            )}
          </div>

          <div style={{ display: 'flex', gap: 12 }}>
            <button
              onClick={onClose}
              disabled={saving}
              style={{
                padding: '12px 24px',
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: 10,
                cursor: saving ? 'not-allowed' : 'pointer',
                fontSize: 14,
                fontWeight: 600,
                color: '#fff',
              }}
            >
              Cancel
            </button>

            {imageUrl && (
              <button
                onClick={handleSave}
                disabled={saving}
                style={{
                  padding: '12px 24px',
                  background: saving ? '#ccc' : '#021a4a',
                  border: 'none',
                  borderRadius: 10,
                  cursor: saving ? 'not-allowed' : 'pointer',
                  fontSize: 14,
                  fontWeight: 600,
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                }}
              >
                <FaCheck size={14} />
                {saving ? 'Saving...' : 'Save'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
